import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import type { SimulationRunSummary } from '../types/simulation';

export interface CachedRunDetails {
  sim: SimulationRunSummary;
  ridePoints: any[] | null;
}

interface SimulationResultsContextValue {
  /** List of runs from "fetch my results" - persists when navigating back to map */
  resultsList: SimulationRunSummary[];
  setResultsList: React.Dispatch<React.SetStateAction<SimulationRunSummary[]>>;
  /** Currently selected run ID - persists when navigating back to map */
  selectedRunId: string | null;
  setSelectedRunId: (runId: string | null) => void;
  /** Cache of loaded run details by run_id - so we don't refetch when returning to the page */
  runDetailsCache: Record<string, CachedRunDetails>;
  setCachedRunDetails: (runId: string, details: CachedRunDetails) => void;
  getCachedRunDetails: (runId: string) => CachedRunDetails | undefined;
  /** Optional handoff defaults for Run Simulation modal after map imports. */
  runSimulationDefaults: {
    rideLayerId: string | null;
    fleetId: string | null;
  };
  setRunSimulationDefaults: (defaults: { rideLayerId: string | null; fleetId: string | null }) => void;
}

const SimulationResultsContext = createContext<SimulationResultsContextValue | null>(null);

export function SimulationResultsProvider({ children }: { children: React.ReactNode }) {
  const [resultsList, setResultsList] = useState<SimulationRunSummary[]>([]);
  const [selectedRunId, setSelectedRunIdState] = useState<string | null>(null);
  const [runDetailsCache, setRunDetailsCache] = useState<Record<string, CachedRunDetails>>({});
  const [runSimulationDefaults, setRunSimulationDefaultsState] = useState<{ rideLayerId: string | null; fleetId: string | null }>({
    rideLayerId: null,
    fleetId: null,
  });

  // Keep a ref so getCachedRunDetails can be stable (no re-creation when cache changes)
  const runDetailsCacheRef = useRef(runDetailsCache);
  runDetailsCacheRef.current = runDetailsCache;

  const setSelectedRunId = useCallback((runId: string | null) => {
    setSelectedRunIdState(runId);
  }, []);

  const setCachedRunDetails = useCallback((runId: string, details: CachedRunDetails) => {
    setRunDetailsCache((prev) => ({ ...prev, [runId]: details }));
  }, []);
  const setRunSimulationDefaults = useCallback((defaults: { rideLayerId: string | null; fleetId: string | null }) => {
    setRunSimulationDefaultsState(defaults);
  }, []);

  // Reads from the ref so the function identity never changes, preventing consumers
  // from re-running effects purely because the cache was written to.
  const getCachedRunDetails = useCallback(
    (runId: string) => runDetailsCacheRef.current[runId],
    []
  );

  const value = useMemo<SimulationResultsContextValue>(
    () => ({
      resultsList,
      setResultsList,
      selectedRunId,
      setSelectedRunId,
      runDetailsCache,
      setCachedRunDetails,
      getCachedRunDetails,
      runSimulationDefaults,
      setRunSimulationDefaults,
    }),
    [
      resultsList,
      setResultsList,
      selectedRunId,
      setSelectedRunId,
      runDetailsCache,
      setCachedRunDetails,
      getCachedRunDetails,
      runSimulationDefaults,
      setRunSimulationDefaults,
    ]
  );

  return (
    <SimulationResultsContext.Provider value={value}>
      {children}
    </SimulationResultsContext.Provider>
  );
}

export function useSimulationResultsContext() {
  const ctx = useContext(SimulationResultsContext);
  return ctx;
}
